import { type FC, type RefObject } from 'react';

import {
  IconArrowLeft,
  IconMenu2,
  IconPlayerPlay,
} from '@tabler/icons-react';
import {
  ButtonField,
  ButtonFieldIcon,
  PillSuggestion,
} from '@wk/components-react16';

import { cn } from '@/lib/utils';
import type { Application } from '@/types';

import SubscribeBellPopover from './SubscribeBellPopover';

interface ApplicationDetailsHeaderProps {
  application?: Application;
  applicationName?: string;
  isMobileViewport: boolean;
  mobileRunButtonRef: RefObject<HTMLButtonElement | null>;
  selectedEnvironment: string;
  selectedEnvironmentAcronym: string;
  selectedEnvironmentKey: string;
  onBack: () => void;
  onOpenMobileDetails: () => void;
  onOpenRunEvaluation: () => void;
}

const ApplicationDetailsHeader: FC<ApplicationDetailsHeaderProps> = ({
  application,
  applicationName,
  isMobileViewport,
  mobileRunButtonRef,
  selectedEnvironment,
  selectedEnvironmentAcronym,
  selectedEnvironmentKey,
  onBack,
  onOpenMobileDetails,
  onOpenRunEvaluation,
}) => (
  <div className="flex w-full items-center justify-between gap-3">
    <div className="flex min-w-0 items-center gap-4">
      <ButtonFieldIcon size="small24px">
        <button onClick={onBack}>
          <IconArrowLeft />
        </button>
      </ButtonFieldIcon>
      <div className="flex min-w-0 items-center gap-2">
        <h1 className="min-w-0 font-medium whitespace-normal break-words leading-tight sm:truncate">
          {applicationName}
        </h1>
        {isMobileViewport ? (
          <span
            className={cn([
              'envBadge',
              `envBadge--${selectedEnvironmentKey}`,
            ])}
          >
            <PillSuggestion size="small">
              {selectedEnvironmentAcronym}
            </PillSuggestion>
          </span>
        ) : null}
      </div>
    </div>
    {isMobileViewport ? (
      <div className="flex items-center gap-2">
        {application ? (
          <SubscribeBellPopover
            bitId={application.bitId}
            applicationName={application.name}
            environment={selectedEnvironment}
            compact
          />
        ) : null}
        <ButtonField mode="ghost" size="small">
          <button
            type="button"
            aria-label="Open details"
            onClick={onOpenMobileDetails}
          >
            <IconMenu2 size={14} />
          </button>
        </ButtonField>
        <ButtonField mode="ghost" size="small">
          <button
            ref={mobileRunButtonRef}
            className="application-details-mobile-run-trigger"
            aria-label="Run Evaluation"
            onClick={onOpenRunEvaluation}
          >
            <IconPlayerPlay size={14} />
          </button>
        </ButtonField>
      </div>
    ) : null}
  </div>
);

export default ApplicationDetailsHeader;
